import { type RefObject } from 'preact';

import { useCallback, useEffect, useRef } from 'preact/hooks';

// TODO: TONCONNECT. Remove patch when package works with preact
import {
  type SendTransactionRequest,
  useTonConnectModal,
  useTonConnectUI,
} from '@tonconnect/ui-react/packages/ui-react/lib/index.cjs';
import WebApp from '@twa-dev/sdk';

import { useMoneyTagAds } from '@/app/hooks/ads/use-moneytag-ads';
import { usePrioritizedAdvertisement } from '@/app/hooks/ads/use-prioritized-advertisements';

import { getScore, sendGameProgress, setScore } from '@/shared/api/game';
import { type Game } from '@/shared/api/game/types';
import { updateUserWalletProfile } from '@/shared/api/user';
import { isObjectEmpty } from '@/shared/helpers/is-object-empty';
import { closeWebapp } from '@/shared/helpers/telegram/close-webapp';
import { openTgLinkHandler } from '@/shared/helpers/telegram/open-tg-link-handler';
import { returnToMarket } from '@/shared/helpers/telegram/return-to-market';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { makeShareEvent } from '@/shared/service/analytics/events/click-share-game-event';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';
import { useAppState } from '@/shared/stores/app-store/app-store';

import { DEFAULT_PROGRESS } from '../constants';
import { gameAnalyticEventNewPaymentHandler } from '../lib/analytics/game-analytic-event-new-payment-handler';
import { gameAnalyticEventNewSessionHandler } from '../lib/analytics/game-analytic-event-new-session-handler';
import { gameAnalyticEventSendHandler } from '../lib/analytics/game-analytic-event-send-handler';
import { useCustomShareHandler } from '../lib/custom-share-handler';
import { getDataHandler } from '../lib/get-data-handler';
import { getPaymentInfoHandler } from '../lib/get-payment-info-handler';
import { getRespond } from '../lib/get-responder';
import { getShareLinkHandler } from '../lib/get-share-link-handler';
import { getUserProfileHandler } from '../lib/get-user-profile-handler';
import { requestPaymentHandler } from '../lib/request-payment-handler';
import { type Responder } from '../lib/respond-with-message';
import { setDataHandler } from '../lib/set-data-handler';
import { showCirclesScoreModal } from '../lib/show-circles-score-modal';
import { validateMessageData } from '../lib/validate-message-data';
import { warnIncorrectValues } from '../lib/warn-incorrect-values';
import {
  usePlaydeckActions,
  usePlaydeckOpen,
  usePlaydeckState,
} from '../models/playdeck.store';
import { type MessageData, Method } from '../types';

interface PostMessage {
  data: MessageData;
}

interface UseIFrameCommunicationProps {
  gameIframeRef: RefObject<HTMLIFrameElement>;
  game: Game | null;
}

const NANOTONS_IN_TON = 1_000_000_000;

export const useIFrameCommunication = ({
  gameIframeRef,
  game,
}: UseIFrameCommunicationProps) => {
  const { token, sessionId, startAppArgs, lang, userProfile } = useAppState();
  const { setProgress, setResponder, setGameControlsBackButton } = usePlaydeckActions();
  const backButtonHandlerRef = useRef<(() => void) | null>(null);
  const isOpen = usePlaydeckOpen();
  const { respond } = usePlaydeckState();
  const customSharing = useCustomShareHandler();

  const { show: showAd } = usePrioritizedAdvertisement('game');
  const { showBanner: showAdBanner } = useMoneyTagAds();

  const [tonConnectUI] = useTonConnectUI();
  const { open: openTonConnectModal } = useTonConnectModal();

  const { isTestUser, isKassir } = useFeatureFlag();

  // Enable Telegram event forwarding only when pdDisableTma=true query param is present
  const enableTelegramForwarding =
    new URLSearchParams(window.location.search).get('pdDisableTma') === 'true';

  const handleTonTransaction = async (value: { amount: number }, respond: Responder) => {
    try {
      const wallet = tonConnectUI.account;

      if (!wallet) {
        openTonConnectModal();

        const unsubscribe = tonConnectUI.onStatusChange(async (wallet) => {
          if (wallet) {
            try {
              await updateUserWalletProfile(wallet?.account);
            } catch (error) {
              console.error('Error updating user wallet profile', error);
            }
            unsubscribe();
          }
        });

        respond({
          method: Method.RESPOND_TRANSACTION_STATUS,
          status: 'wallet_not_connected',
        });
        return;
      }

      // Конвертируем TON в наноTON
      const amountInNano = Math.floor(value.amount * NANOTONS_IN_TON).toString();
      const walletAddress = import.meta.env.VITE_TON_ADDRESS;

      const transaction: SendTransactionRequest = {
        validUntil: Date.now() + 5 * 60 * 1000, // 5 минут
        messages: [
          {
            address: walletAddress,
            amount: amountInNano,
          },
        ],
      };

      const result = await tonConnectUI.sendTransaction(transaction);

      respond({
        method: Method.RESPOND_TRANSACTION_STATUS,
        status: 'success',
        result,
      });
    } catch (error) {
      respond({
        method: Method.RESPOND_TRANSACTION_STATUS,
        status: 'error',
        error,
      });
    }
  };

  const showAdHandler = useCallback(
    (respond: Responder, value: any) => {
      respond({ method: Method.START_AD });
      showAd(value, {
        onReward: () => {
          respond({ method: Method.REWARDED_AD, value: null });
        },
        onError: (error: Error & { code?: Error }) => {
          respond({
            method: Method.ERR_AD,
            value: error?.message ?? error?.code ?? error,
          });
        },
        onSkip: () => {
          respond({ method: Method.SKIP_AD });
        },
      });
    },
    [showAd]
  );

  const showAdBannerHandler = useCallback(
    (respond: Responder, value: any) => {
      showAdBanner(value?.config);
      respond({ method: Method.SHOW_AD_BANNER });
    },
    [showAdBanner]
  );

  /**
   * Forward Telegram WebApp events to the iframe.
   * Telegram sends events (like invoice_closed, popup_closed, etc.) only to the parent window.
   * This function intercepts those events and forwards them to the iframe so that
   * @twa-dev/sdk inside the iframe can properly handle callbacks.
   *
   * Telegram uses two mechanisms:
   * 1. Web version: postMessage with JSON string {eventType, eventData}
   * 2. Desktop/Mobile: direct call to window.Telegram.WebView.receiveEvent()
   */
  const forwardTelegramEvents = useCallback(
    (event: MessageEvent) => {
      if (!enableTelegramForwarding) return;

      const iframe = gameIframeRef.current;
      if (!iframe?.contentWindow) return;

      // Ignore messages from the iframe itself
      if (event.source === iframe.contentWindow) return;

      // Telegram SDK sends data as JSON string, need to parse it
      let eventData = event.data;
      if (typeof eventData === 'string') {
        try {
          eventData = JSON.parse(eventData);
        } catch {
          // Not a JSON string, ignore
          return;
        }
      }

      // Check if this is a Telegram WebApp event (has eventType field)
      if (eventData?.eventType) {
        console.log('[TELEGRAM EVENT FORWARD] postMessage to iframe:', eventData);

        // Forward the event to iframe in the same format (as JSON string, as Telegram expects)
        iframe.contentWindow.postMessage(JSON.stringify(eventData), '*');
      }
    },
    [gameIframeRef, enableTelegramForwarding]
  );

  /**
   * Forward events from Telegram native app (Desktop/Mobile).
   * Telegram calls receiveEvent directly, we need to forward it to iframe via postMessage.
   */
  const forwardEventToIframe = useCallback(
    (eventType: string, eventData: unknown) => {
      if (!enableTelegramForwarding) return;

      const iframe = gameIframeRef.current;
      if (!iframe?.contentWindow) return;

      console.log(
        '[TELEGRAM EVENT FORWARD] receiveEvent to iframe:',
        eventType,
        eventData
      );

      // Forward to iframe as JSON string (format expected by @twa-dev/sdk)
      iframe.contentWindow.postMessage(JSON.stringify({ eventType, eventData }), '*');
    },
    [gameIframeRef, enableTelegramForwarding]
  );

  // Patch Telegram's receiveEvent to forward events to iframe (for Desktop/Mobile)
  useEffect(() => {
    if (!enableTelegramForwarding) return;

    const iframe = gameIframeRef.current;
    if (!iframe) return;

    // Store original receiveEvent functions
    const originalTelegramReceiveEvent = (window as any).Telegram?.WebView?.receiveEvent;
    const originalGameProxyReceiveEvent = (window as any).TelegramGameProxy?.receiveEvent;

    // Patch window.Telegram.WebView.receiveEvent (iOS/Android)
    if ((window as any).Telegram?.WebView) {
      const originalFn = (window as any).Telegram.WebView.receiveEvent;
      (window as any).Telegram.WebView.receiveEvent = (
        eventType: string,
        eventData: unknown
      ) => {
        console.log(
          '[TELEGRAM EVENT FORWARD] Intercepted Telegram.WebView.receiveEvent:',
          eventType,
          eventData
        );
        forwardEventToIframe(eventType, eventData);
        if (originalFn) {
          originalFn.call((window as any).Telegram.WebView, eventType, eventData);
        }
      };
    }

    // Patch window.TelegramGameProxy.receiveEvent (Desktop)
    if ((window as any).TelegramGameProxy) {
      const originalFn = (window as any).TelegramGameProxy.receiveEvent;
      (window as any).TelegramGameProxy.receiveEvent = (
        eventType: string,
        eventData: unknown
      ) => {
        console.log(
          '[TELEGRAM EVENT FORWARD] Intercepted TelegramGameProxy.receiveEvent:',
          eventType,
          eventData
        );
        forwardEventToIframe(eventType, eventData);
        if (originalFn) {
          originalFn.call((window as any).TelegramGameProxy, eventType, eventData);
        }
      };
    }

    return () => {
      // Restore original functions on cleanup
      if ((window as any).Telegram?.WebView && originalTelegramReceiveEvent) {
        (window as any).Telegram.WebView.receiveEvent = originalTelegramReceiveEvent;
      }
      if ((window as any).TelegramGameProxy && originalGameProxyReceiveEvent) {
        (window as any).TelegramGameProxy.receiveEvent = originalGameProxyReceiveEvent;
      }
    };
  }, [forwardEventToIframe]);

  useEffect(() => {
    if (!gameIframeRef.current) return;

    const respond = getRespond(gameIframeRef);

    setResponder(respond);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFramesCommunication = useCallback(
    async ({ data }: PostMessage) => {
      if (!data || !data['playdeck'] || !game) {
        return;
      }

      const { method, value, text, progress } = data['playdeck'];

      if (isTestUser || isKassir) {
        console.warn('[TO PLAYDECK] POST MESSAGE: ', data['playdeck']);
      }

      const respond = getRespond(gameIframeRef);
      setResponder(respond);

      switch (method) {
        case Method.LOADING: {
          const percent = value?.percent;

          const allowedTypes = ['number', 'undefined'];

          if (!validateMessageData({ value: percent ?? value, allowedTypes })) {
            warnIncorrectValues(Method.LOADING, 'percent', allowedTypes);
          }

          const progressValue = percent ?? value ?? DEFAULT_PROGRESS;
          setProgress(progressValue);
          break;
        }

        case Method.GET_USER:
          respond({
            method: Method.GET_USER,
            value: {
              id: userProfile?.telegramId,
              username: userProfile?.username,
              token,
            },
          });
          break;

        case Method.GET_SCORE:
          respond({ method: Method.GET_SCORE, value: await getScore() });
          break;

        case Method.SET_SCORE: {
          await setScore(value);
          respond({ method: Method.SET_SCORE, value: null });
          break;
        }

        case Method.SHOW_SCORE: {
          await showCirclesScoreModal({
            score: Number(value),
            gameKey: game.key,
            telegramId: userProfile!.telegramId,
          });
          respond({ method: Method.REPLAY });
          break;
        }

        case Method.GET_USER_PROFILE:
          await getUserProfileHandler(startAppArgs, sessionId, token, lang.game, respond);
          break;

        case Method.GET_DATA: {
          await getDataHandler(value, data, respond);
          break;
        }

        case Method.SET_DATA: {
          setDataHandler(value, data);
          break;
        }

        case Method.GET_USER_LOCALE:
          respond({
            method: Method.GET_USER_LOCALE,
            value: lang.game,
          });
          break;

        case Method.GET_TOKEN:
          respond({ method: Method.GET_TOKEN, value: { token } });
          break;

        case Method.CUSTOM_SHARE:
          customSharing(value, text);

          break;

        case Method.GET_SHARE_LINK: {
          getShareLinkHandler(value, game.key, respond);
          break;
        }

        case Method.SEND_ANALYTICS: {
          gameAnalyticEventSendHandler(value, sessionId);
          break;
        }

        case Method.GET_PLAYDECK_STATE:
          respond({ method: Method.GET_PLAYDECK_STATE, value: isOpen });
          break;

        case Method.OPEN_TG_LINK: {
          openTgLinkHandler(value, respond);
          break;
        }

        case Method.OPEN_LINK: {
          const options = value.options ?? {};
          WebApp.openLink(value.link, options);
          break;
        }

        case Method.CLOSE_WEB_APP:
          closeWebapp();
          break;

        case Method.GET_URL_PARAMS: {
          if (!startAppArgs) break;

          const gameParams = startAppArgs?.game?.raw;
          respond({ method: Method.GET_URL_PARAMS, value: gameParams });
          break;
        }

        case Method.RETURN_TO_MP:
          returnToMarket();
          break;

        case Method.AL_NEW_SESSION: {
          gameAnalyticEventNewSessionHandler(value, sessionId, userProfile);
          break;
        }

        case Method.AL_NEW_PAYMENT: {
          gameAnalyticEventNewPaymentHandler(value, sessionId, userProfile);
          break;
        }

        case Method.SEND_GAME_PROGRESS: {
          if (!progress && !value) {
            respond({ method: Method.SEND_GAME_PROGRESS, error: 'No progress data' });
            break;
          }
          const result = await sendGameProgress(progress ?? value);
          respond({ method: Method.SEND_GAME_PROGRESS, value: result });
          break;
        }

        case Method.REQUEST_PAYMENT: {
          await requestPaymentHandler(value, respond);
          break;
        }

        case Method.GET_PAYMENT_INFO: {
          await getPaymentInfoHandler(value, respond);
          break;
        }

        case Method.SHOW_AD:
          if (userProfile?.tests?.noAd) {
            respond({ method: Method.START_AD });
            respond({ method: Method.REWARDED_AD, value: null });
            break;
          }
          showAdHandler(respond, value);
          break;

        case Method.SHOW_AD_BANNER:
          if (userProfile?.tests?.noAd) {
            respond({ method: Method.SHOW_AD_BANNER });
            break;
          }
          showAdBannerHandler(respond, value);
          break;

        case Method.SEND_TON_TRANSACTION: {
          await handleTonTransaction(value, respond);
          break;
        }

        case Method.GET_INIT_DATA: {
          respond({
            method: Method.GET_INIT_DATA,
            value: { initData: WebApp.initData, initDataUnsafe: WebApp.initDataUnsafe },
          });
          break;
        }

        case Method.HAPTIC_FEEDBACK: {
          const allowed: (keyof typeof WebApp.HapticFeedback)[] = [
            'impactOccurred',
            'notificationOccurred',
            'selectionChanged',
          ];
          const type = value?.type as keyof typeof WebApp.HapticFeedback | undefined;
          if (type && allowed.includes(type)) {
            const haptic = WebApp.HapticFeedback;
            if (type === 'selectionChanged') {
              haptic.selectionChanged();
            } else if (type === 'impactOccurred') {
              haptic.impactOccurred(
                value.style as 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
              );
            } else {
              haptic.notificationOccurred(
                value.notificationType as 'error' | 'success' | 'warning'
              );
            }
          }
          break;
        }

        case Method.GET_PLATFORM: {
          respond({ method: Method.GET_PLATFORM, value: WebApp.platform ?? 'unknown' });
          break;
        }

        case Method.SHARE_MESSAGE: {
          WebApp.shareMessage(value.messageId, (hasSent) => {
            const event = makeShareEvent('ingame', game.key, hasSent);
            AnalyticsService.send(event);

            respond({
              method: Method.SHARE_MESSAGE,
              value: { hasSent, sentMessageId: value.messageId },
            });
          });
          break;
        }

        case Method.BACK_BUTTON_SHOW: {
          const handler = () => {
            getRespond(gameIframeRef)({ method: Method.BACK_BUTTON_CLICKED });
          };
          if (backButtonHandlerRef.current) {
            WebApp.BackButton.offClick(backButtonHandlerRef.current);
          }
          backButtonHandlerRef.current = handler;
          WebApp.BackButton.onClick(handler);
          WebApp.BackButton.show();
          setGameControlsBackButton(true);
          break;
        }

        case Method.BACK_BUTTON_HIDE: {
          if (backButtonHandlerRef.current) {
            WebApp.BackButton.offClick(backButtonHandlerRef.current);
            backButtonHandlerRef.current = null;
          }
          setGameControlsBackButton(false);
          break;
        }

        default:
          ErrorLogger.info(`Unknown method: ${method}`);
          break;
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setProgress, game, gameIframeRef, respond, isOpen, userProfile]
  );

  useEffect(() => {
    if (!game) {
      ErrorLogger.info('useEffect: game object is null, not adding event listener.');
      return;
    }

    ErrorLogger.info(
      `useEffect: Adding event listener for "message". Game key: ${game?.key ?? 'N/A'}. UserProfile initially empty: ${isObjectEmpty(userProfile)}`
    );
    window.addEventListener('message', handleFramesCommunication);
    window.addEventListener('message', forwardTelegramEvents);

    return () => {
      ErrorLogger.info(
        `useEffect: Removing event listener for "message". Game key: ${game?.key ?? 'N/A'}`
      );
      window.removeEventListener('message', handleFramesCommunication);
      window.removeEventListener('message', forwardTelegramEvents);

      if (backButtonHandlerRef.current) {
        WebApp.BackButton.offClick(backButtonHandlerRef.current);
        backButtonHandlerRef.current = null;
        setGameControlsBackButton(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, userProfile]); // Dependencies updated
};
